/**
 * Assign Container Modal
 *
 * Modal dialog for assigning containers to jobs.
 * Includes job selection and deployment date picker.
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAssignContainer } from '@/hooks/useContainers';
import { assignContainerSchema, type AssignContainerDTO } from '@/schemas/containerSchema';
import type { Container } from '@/services/resource-container.service';

interface AssignContainerModalProps {
  open: boolean;
  onClose: () => void;
  container: Container | null;
}

export function AssignContainerModal({
  open,
  onClose,
  container,
}: AssignContainerModalProps) {
  const assignContainer = useAssignContainer();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AssignContainerDTO>({
    resolver: zodResolver(assignContainerSchema),
    defaultValues: {
      jobId: '',
      jobAddress: '',
      deploymentDate: new Date().toISOString().slice(0, 16), // YYYY-MM-DDTHH:mm
    },
  });

  const onSubmit = async (data: AssignContainerDTO) => {
    if (!container) return;

    try {
      await assignContainer.mutateAsync({
        id: container.id,
        data,
      });
      reset();
      onClose();
    } catch (error) {
      // Error toast handled by mutation
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!container) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Assign Container to Job</DialogTitle>
          <DialogDescription>
            Assign container {container.containerNumber} ({container.size}) to a job.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Container Info Display */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Container:</span>
                <span className="ml-2 font-medium">{container.containerNumber}</span>
              </div>
              <div>
                <span className="text-gray-600">Size:</span>
                <span className="ml-2 font-medium">{container.size}</span>
              </div>
              <div>
                <span className="text-gray-600">Type:</span>
                <span className="ml-2 font-medium capitalize">{container.type}</span>
              </div>
              <div>
                <span className="text-gray-600">Current Location:</span>
                <span className="ml-2 font-medium capitalize">
                  {container.location.type.replace('_', ' ')}
                </span>
              </div>
            </div>
          </div>

          {/* Job ID */}
          <div className="space-y-2">
            <label htmlFor="jobId" className="block text-sm font-medium text-gray-900">
              Job ID <span className="text-red-600">*</span>
            </label>
            <input
              {...register('jobId')}
              id="jobId"
              type="text"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter job UUID"
            />
            {errors.jobId && (
              <p className="text-sm text-red-600">{errors.jobId.message}</p>
            )}
            <p className="text-xs text-gray-500">
              Enter the UUID of the job to assign this container to
            </p>
          </div>

          {/* Job Address */}
          <div className="space-y-2">
            <label htmlFor="jobAddress" className="block text-sm font-medium text-gray-900">
              Job Address <span className="text-red-600">*</span>
            </label>
            <textarea
              {...register('jobAddress')}
              id="jobAddress"
              rows={2}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="123 Job Site Ave, City, State 12345"
            />
            {errors.jobAddress && (
              <p className="text-sm text-red-600">{errors.jobAddress.message}</p>
            )}
          </div>

          {/* Deployment Date */}
          <div className="space-y-2">
            <label
              htmlFor="deploymentDate"
              className="block text-sm font-medium text-gray-900"
            >
              Deployment Date & Time <span className="text-red-600">*</span>
            </label>
            <input
              {...register('deploymentDate')}
              id="deploymentDate"
              type="datetime-local"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            {errors.deploymentDate && (
              <p className="text-sm text-red-600">{errors.deploymentDate.message}</p>
            )}
            <p className="text-xs text-gray-500">
              When the container will be deployed to the job site
            </p>
          </div>

          {/* Footer Actions */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Assigning...' : 'Assign to Job'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
